import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import { Security, CheckCircle } from '@mui/icons-material';
import mfaApiService from 'api/MfaApiService';
import MfaSetup from '../MfaSetup/MfaSetup';
import { useSelector } from 'store/hooks';
import './MfaSettings.scss';

const MfaSettings: React.FC = () => {
  const history = useHistory();
  const appState = useSelector((state) => state.app);
  const userIsAdmin = appState.roles?.includes('admin');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetupCompleted, setMfaSetupCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showDisableDialog, setShowDisableDialog] = useState(false);

  useEffect(() => {
    loadMfaStatus();
  }, []);

  const loadMfaStatus = async () => {
    try {
      setLoading(true);
      const status = await mfaApiService.getMfaStatus();
      setMfaEnabled(status.mfaEnabled);
      setMfaSetupCompleted(status.mfaSetupCompleted);

      // If MFA is enabled by admin but not set up, automatically show setup dialog
      if (status.mfaEnabled && !status.mfaSetupCompleted) {
        setShowSetupDialog(true);
      }
    } catch (err: any) {
      setError('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMfa = () => {
    setShowSetupDialog(true);
  };

  const renderMfaActionButton = () => {
    if (!mfaEnabled) {
      return (
        <Button variant="contained" color="primary" onClick={handleEnableMfa} startIcon={<Security />}>
          Enable MFA
        </Button>
      );
    }

    if (userIsAdmin) {
      return (
        <Button variant="outlined" color="error" onClick={() => setShowDisableDialog(true)}>
          Disable MFA
        </Button>
      );
    }

    return null;
  };

  const handleDisableMfa = async () => {
    try {
      if (!userIsAdmin) {
        setError('Only an administrator can disable MFA.');
        return;
      }
      setActionLoading(true);
      setError(null);
      await mfaApiService.disableMfa();
      setMfaEnabled(false);
      setMfaSetupCompleted(false);
      setSuccess('MFA has been disabled');
      setShowDisableDialog(false);

      // Clear MFA check status
      sessionStorage.removeItem('mfa_check_done');
      sessionStorage.removeItem('mfa_check_result');
      sessionStorage.setItem('mfa_check_result', 'passed');

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable MFA');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetupSuccess = () => {
    setShowSetupDialog(false);
    setMfaEnabled(true);
    setMfaSetupCompleted(true);
    setSuccess('MFA has been successfully enabled! Redirecting...');

    // Clear MFA check status so user can navigate freely
    sessionStorage.setItem('mfa_check_result', 'passed');
    sessionStorage.setItem('mfa_check_done', 'true');

    console.log('MFA setup completed successfully, reloading to dashboard');

    // Use window.location.href to force a full page reload
    // This ensures the app fetches fresh user data with updated MFA flags
    setTimeout(() => {
      const customerSlug = appState.customer?.slug;
      if (customerSlug) {
        console.log('Redirecting to:', `/${customerSlug}/dashboard`);
        window.location.href = `/${customerSlug}/dashboard`;
      } else {
        console.log('Customer slug not found, reloading page');
        window.location.href = '/';
      }
    }, 1500);
  };

  const handleSetupCancel = () => {
    // Only allow cancel if MFA is not required (not enabled or already completed)
    if (!mfaEnabled || mfaSetupCompleted) {
      setShowSetupDialog(false);
    }
  };

  if (loading) {
    return (
      <Box className="mfa-settings-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="mfa-settings-container">
      <Paper elevation={2} className="mfa-settings-paper">
        <Box className="mfa-settings-header">
          <Security className="mfa-icon" />
          <Typography variant="h6">Two-Factor Authentication (MFA)</Typography>
        </Box>

        {error && (
          <Alert severity="error" className="mfa-alert" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="mfa-alert" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Box className="mfa-settings-content">
          <Box className="mfa-status-section">
            <Box className="mfa-status-info">
              <Typography variant="body1" className="mfa-status-title">
                Status:
              </Typography>
              {mfaEnabled ? (
                <Chip icon={<CheckCircle />} label="Enabled" color="success" size="small" />
              ) : (
                <Chip label="Disabled" color="default" size="small" />
              )}
            </Box>

            {mfaEnabled && (
              <Typography variant="body2" color="textSecondary">
                Your account is protected with time-based one-time passwords (TOTP)
              </Typography>
            )}
          </Box>

          <Box className="mfa-description">
            <Typography variant="body2">
              Two-factor authentication adds an extra layer of security to your account. When enabled, you will need to
              enter a code from your authenticator app in addition to your password when logging in.
            </Typography>
          </Box>

          <Box className="mfa-actions">{renderMfaActionButton()}</Box>

          {mfaEnabled && !userIsAdmin && (
            <Alert severity="info" className="mfa-recovery-info">
              <Typography variant="body2">
                <strong>MFA is enabled.</strong> Only an administrator can disable MFA for your account.
              </Typography>
            </Alert>
          )}

          {mfaEnabled && (
            <Alert severity="info" className="mfa-recovery-info">
              <Typography variant="body2">
                <strong>Important:</strong> If you lose access to your authenticator app, contact your administrator to
                disable MFA for your account.
              </Typography>
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Setup Dialog */}
      <Dialog
        open={showSetupDialog}
        onClose={handleSetupCancel}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={mfaEnabled && !mfaSetupCompleted}
      >
        <DialogContent>
          {mfaEnabled && !mfaSetupCompleted && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>MFA Setup Required:</strong> Your administrator has enabled multi-factor authentication for your
                account. You must complete the setup to continue using the application.
              </Typography>
            </Alert>
          )}
          <MfaSetup
            onSuccess={handleSetupSuccess}
            onCancel={mfaEnabled && !mfaSetupCompleted ? undefined : handleSetupCancel}
          />
        </DialogContent>
      </Dialog>

      {/* Disable Confirmation Dialog */}
      {userIsAdmin && (
        <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)}>
          <DialogTitle>Disable Two-Factor Authentication?</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to disable MFA? This will make your account less secure.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDisableDialog(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleDisableMfa} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : 'Disable MFA'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default MfaSettings;
